import * as React from 'react';
import { withTiming, useSharedValue, } from 'react-native-reanimated';
import { View } from 'react-native';
import { rem } from 'design-system/values';
import { IconSettings } from 'design-system/icons/IconSettings';
import { PickerModal } from 'design-system/picker/PickerModal';
import ChartLoadingOverlay from 'design-system/LineChart/ChartLoadingOverlay';
import ChartOpacityOverlay from 'design-system/LineChart/ChartOpacityOverlay';
import { OVERVIEW_DEFAULT_PERIODS } from 'design-system/LineChart/useLineGraphData';
import ScrollableTimeFrameController from 'design-system/LineChart/ScrollableTimeFrameController';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { ChartType, CHART_TYPE_ICONS, CHART_TYPE_TITLE, } from '../types';
import { isWeb } from '../../../constants';
import OverviewPieChart from './OverviewPieChart';
import AccountsLineChart, { CHART_HEIGHT } from './AccountsLineChart';
const PADDING_HORIZONTAL = rem(16);
const SETTINGS_BTN_WIDTH = rem(56);
const SETTINGS_BTN_SIZE = rem(40);
export const CHART_TYPE_OPTIONS = [
    {
        value: ChartType.line,
        icon: CHART_TYPE_ICONS[ChartType.line],
        label: CHART_TYPE_TITLE[ChartType.line],
    },
    {
        value: ChartType.pie,
        icon: CHART_TYPE_ICONS[ChartType.pie],
        label: CHART_TYPE_TITLE[ChartType.pie],
    },
    {
        value: ChartType.assetVsDebt,
        icon: CHART_TYPE_ICONS[ChartType.assetVsDebt],
        label: CHART_TYPE_TITLE[ChartType.assetVsDebt],
    },
];
const AccountChartView = ({ title, pieData, chartType, requesting, setChartType, selectedPeriod, selectedInfoObj, selectedInfoObj2, portfolioDataSet, portfolioDataSet2, setSelectedPeriod, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const viewRef = React.useRef(null);
    const opacity = useSharedValue(1);
    const [renderGraph, setRenderGraph] = React.useState(false);
    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setRenderGraph(true);
            opacity.value = withTiming(0, { duration: 100 });
        }, 350);
        return () => {
            clearTimeout(timeout);
        };
    }, [opacity]);
    const Options = React.useMemo(() => {
        if (title === 'Net Worth - Main') {
            return [CHART_TYPE_OPTIONS[0], CHART_TYPE_OPTIONS[2]];
        }
        return [CHART_TYPE_OPTIONS[0], CHART_TYPE_OPTIONS[1]];
    }, [title]);
    const handleSettingsPress = React.useCallback(() => {
        PickerModal.show({
            hideAfterCallback: true,
            pickerOptions: Options,
            selectedIconFill: colors.elements.brand,
            selectedValue: CHART_TYPE_OPTIONS.find((item) => item.value === chartType),
            onSelected: (selected) => {
                setChartType(selected.value);
            },
        }, {
            title: 'Chart type',
            ...(isWeb && {
                containerStyle: colors.shadows.cardLarge,
            }),
        }, viewRef);
    }, [
        Options,
        chartType,
        colors.elements.brand,
        colors.shadows.cardLarge,
        setChartType,
    ]);
    const content = React.useMemo(() => {
        if (!renderGraph)
            return null;
        return (<AccountsLineChart title={title} chartType={chartType} selectedPeriod={selectedPeriod} selectedInfoObj={selectedInfoObj} selectedInfoObj2={selectedInfoObj2} portfolioDataSet={portfolioDataSet} portfolioDataSet2={portfolioDataSet2}/>);
    }, [
        chartType,
        portfolioDataSet,
        portfolioDataSet2,
        renderGraph,
        selectedInfoObj,
        selectedInfoObj2,
        selectedPeriod,
        title,
    ]);
    const pieChartContent = React.useMemo(() => (<View style={styles.pieChartContainer}>
        <OverviewPieChart title={title} data={pieData}/>
        <View style={styles.floatingSettingsBtn}>
          <TouchableBounce ref={viewRef} onPress={handleSettingsPress} style={styles.settingsContainer}>
            <IconSettings color={colors.elements.secondary}/>
          </TouchableBounce>
        </View>
      </View>), [
        colors.elements.secondary,
        handleSettingsPress,
        pieData,
        styles.floatingSettingsBtn,
        styles.pieChartContainer,
        styles.settingsContainer,
        title,
    ]);
    const lineChartContent = React.useMemo(() => {
        const hasSettings = title !== 'Everyday';
        return (<View style={styles.graphContainer}>
        <View style={styles.graphTop}>
          <View style={styles.chartContainer}>{content}</View>
          <View style={styles.flex}/>
          <ChartOpacityOverlay opacitySharedValue={opacity}/>
          <ChartLoadingOverlay requesting={requesting}/>
        </View>
        <View style={styles.row}>
          <View style={styles.flex}>
            <ScrollableTimeFrameController darkActiveIndicator minusWidth={hasSettings ? SETTINGS_BTN_WIDTH : 0} selectedPeriod={selectedPeriod} periods={OVERVIEW_DEFAULT_PERIODS} paddingHorizontal={PADDING_HORIZONTAL} setSelectedPeriod={setSelectedPeriod}/>
          </View>
          {hasSettings ? (<TouchableBounce ref={viewRef} style={styles.settingsBtn} onPress={handleSettingsPress}>
              <IconSettings color={colors.elements.secondary}/>
            </TouchableBounce>) : null}
        </View>
      </View>);
    }, [
        colors.elements.secondary,
        content,
        handleSettingsPress,
        opacity,
        requesting,
        selectedPeriod,
        setSelectedPeriod,
        styles.chartContainer,
        styles.flex,
        styles.graphContainer,
        styles.graphTop,
        styles.row,
        styles.settingsBtn,
        title,
    ]);
    const chart = React.useMemo(() => {
        switch (chartType) {
            case ChartType.pie: {
                return pieChartContent;
            }
            default: {
                return lineChartContent;
            }
        }
    }, [chartType, pieChartContent, lineChartContent]);
    return chart;
};
export default AccountChartView;
const styleSet = createStyleSheets((colors) => ({
    graphContainer: {
        paddingBottom: rem(2),
    },
    flex: {
        flex: 1,
    },
    chartContainer: {
        height: CHART_HEIGHT,
    },
    graphTop: {
        height: CHART_HEIGHT,
    },
    pieChartContainer: {
        height: rem(331),
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    settingsBtn: {
        marginHorizontal: rem(8),
        borderRadius: rem(8),
        alignItems: 'center',
        justifyContent: 'center',
        width: SETTINGS_BTN_SIZE,
        height: SETTINGS_BTN_SIZE,
        backgroundColor: colors.background.dark,
    },
    floatingSettingsBtn: {
        right: 0,
        bottom: rem(2),
        height: rem(57),
        paddingRight: rem(8),
        position: 'absolute',
        justifyContent: 'center',
    },
    settingsContainer: {
        alignItems: 'center',
        borderRadius: rem(8),
        width: SETTINGS_BTN_SIZE,
        justifyContent: 'center',
        height: SETTINGS_BTN_SIZE,
        backgroundColor: colors.background.dark,
    },
}));
